/*
 * Typo tolerance for theme-native search.
 *
 * Shopify's search/predictive-search APIs do no fuzzy matching: "gooky" returns
 * zero products even though "Goosky" is a brand in the catalogue. This script
 * corrects the term against the store's own vocabulary (vendors, product types,
 * trending terms - see snippets/search-fuzzy-dictionary.liquid) and is used in
 * two places:
 *   1. predictive-search.js - retries the dropdown query with the correction.
 *   2. the /search page      - when the page renders zero results, it reloads
 *                              with the corrected term (`hd_from` carries the
 *                              original so the notice can be shown and no
 *                              redirect loop is possible).
 */
window.HDSearchFuzzy = (function () {
  var HAS_DIGIT = /\d/;
  var SPLIT = /[^a-z0-9]+/;
  var MIN_WORD = 4; // shorter words are too ambiguous to "fix"
  var words = null;
  var cache = {};

  function build() {
    if (words) return;
    var raw = (window.hdSearchFuzzy && window.hdSearchFuzzy.terms) || [];
    var seen = {};
    raw.forEach(function (entry) {
      if (!entry) return;
      String(entry)
        .toLowerCase()
        .split(SPLIT)
        .forEach(function (word) {
          if (word.length >= 3 && !HAS_DIGIT.test(word)) seen[word] = true;
        });
    });
    words = Object.keys(seen);
  }

  /*
   * Damerau-Levenshtein (optimal string alignment) so a transposition such as
   * "gosoky" counts as one edit instead of two. Bails out early once the row
   * minimum exceeds max, which keeps a full dictionary scan cheap.
   */
  function distance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;

    var prev2 = [];
    var prev = [];
    var curr = [];
    var i, j;

    for (j = 0; j <= b.length; j++) prev[j] = j;

    for (i = 1; i <= a.length; i++) {
      curr[0] = i;
      var rowMin = curr[0];
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var value = Math.min(
          curr[j - 1] + 1, // insertion
          prev[j] + 1, // deletion
          prev[j - 1] + cost // substitution
        );
        if (
          i > 1 &&
          j > 1 &&
          a.charAt(i - 1) === b.charAt(j - 2) &&
          a.charAt(i - 2) === b.charAt(j - 1)
        ) {
          value = Math.min(value, prev2[j - 2] + 1); // transposition
        }
        curr[j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev.slice();
      prev = curr.slice();
    }

    return prev[b.length];
  }

  function maxDistanceFor(word) {
    if (word.length <= 4) return 1;
    if (word.length <= 7) return 2;
    return 3;
  }

  // A word that prefixes a real dictionary word is not a typo ("goo" -> goosky).
  function isPrefixOfKnownWord(word) {
    for (var i = 0; i < words.length; i++) {
      if (words[i].indexOf(word) === 0) return true;
    }
    return false;
  }

  function bestMatch(word) {
    var max = maxDistanceFor(word);
    var best = null;
    var bestDistance = max + 1;

    for (var i = 0; i < words.length; i++) {
      var candidate = words[i];
      if (candidate === word) return null; // exact hit, nothing to fix
      var d = distance(word, candidate, max);
      if (d > max) continue;
      // Typos rarely land on the first character, so only trust a differing
      // first letter when it is a single edit away.
      if (candidate.charAt(0) !== word.charAt(0) && d > 1) continue;
      if (
        d < bestDistance ||
        (d === bestDistance && best && candidate.length < best.length)
      ) {
        best = candidate;
        bestDistance = d;
      }
    }

    return best;
  }

  /*
   * Correct a whole phrase word by word. Words with digits (model codes such as
   * "s2", "rs4"), short words and valid prefixes are left untouched.
   */
  function correct(phrase) {
    var term = (phrase || '').toString().trim();
    if (!term) return { changed: false, corrected: term };
    if (cache[term]) return cache[term];

    build();
    var result = { changed: false, corrected: term };

    if (words.length) {
      var changed = false;
      var parts = term.split(/\s+/).map(function (part) {
        var lower = part.toLowerCase();
        if (lower.length < MIN_WORD || HAS_DIGIT.test(lower)) return part;
        if (isPrefixOfKnownWord(lower)) return part;
        var match = bestMatch(lower);
        if (!match) return part;
        changed = true;
        return match;
      });

      if (changed) result = { changed: true, corrected: parts.join(' ') };
    }

    cache[term] = result;
    return result;
  }

  function popularTerms() {
    var list = (window.hdSearchFuzzy && window.hdSearchFuzzy.trending) || [];
    return list.map(String).filter(Boolean);
  }

  /*
   * Pick the popular search closest to what the visitor typed, so a dead end
   * still shows something related ("gooky rs" -> "goosky rs4"). Falls back to the
   * first configured popular search when nothing looks similar.
   */
  function bestPopular(phrase) {
    var list = popularTerms();
    if (!list.length) return null;

    var queryWords = (phrase || '')
      .toLowerCase()
      .split(SPLIT)
      .filter(function (word) {
        return word.length >= 3;
      });

    var best = null;
    var bestScore = 0;

    list.forEach(function (candidate) {
      var candidateWords = candidate.toLowerCase().split(SPLIT);
      var score = 0;

      queryWords.forEach(function (queryWord) {
        var max = maxDistanceFor(queryWord);
        candidateWords.forEach(function (candidateWord) {
          if (candidateWord === queryWord) score += 3;
          else if (candidateWord.indexOf(queryWord) === 0 || queryWord.indexOf(candidateWord) === 0) score += 2;
          else if (distance(queryWord, candidateWord, max) <= max) score += 1;
        });
      });

      if (score > bestScore) {
        bestScore = score;
        best = candidate;
      }
    });

    return best || list[0];
  }

  function headingFor(term) {
    var template =
      (window.search_strings && window.search_strings.products_for) || 'Products for “[terms]”';
    var node = document.createElement('h3');
    node.className = 'search-block-title quickSearchFallback__heading text-left';
    node.innerHTML = '<span class="text"></span>';
    node.querySelector('.text').textContent = template.replace('[terms]', term);
    return node;
  }

  function noticeHtml(original, corrected) {
    var template =
      (window.search_strings && window.search_strings.corrected_notice) ||
      'No results for “[original]”. Showing results for “[corrected]”.';
    var text = template
      .replace('[original]', original)
      .replace('[corrected]', corrected);
    var node = document.createElement('p');
    node.className = 'hd-search-corrected';
    node.textContent = text;
    return node;
  }

  return {
    correct: correct,
    noticeHtml: noticeHtml,
    popularTerms: popularTerms,
    bestPopular: bestPopular,
    headingFor: headingFor
  };
})();

/*
 * /search page behaviour. main-search.liquid exposes window.hdSearchNoResults
 * when a performed search returned nothing.
 */
(function () {
  var FALLBACK_LIMIT = 8;

  /*
   * Still nothing to show: load the products of the closest popular search into
   * the empty state so the page is never a dead end. Uses the dropdown's own
   * template (`view=ajax_dropdown`); the Section Rendering API is not an option
   * because /search?section_id=main answers 404.
   */
  function loadFallbackGrid(typedTerm) {
    var holder = document.querySelector('[data-search-fallback-results]');
    if (!holder || holder.dataset.loaded) return;

    var term = window.HDSearchFuzzy.bestPopular(typedTerm);
    if (!term) return;
    holder.dataset.loaded = '1';

    var searchUrl = (window.routes && window.routes.search_url) || '/search';
    var query =
      '?q=' + encodeURIComponent(term) + '&type=product&options%5Bprefix%5D=last';

    fetch(searchUrl + query + '&view=ajax_dropdown')
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        if (!text) return;
        var grid = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('.products-grid');
        if (!grid || !grid.children.length) return;

        while (grid.children.length > FALLBACK_LIMIT) {
          grid.removeChild(grid.lastElementChild);
        }

        var viewAll = document.createElement('div');
        viewAll.className = 'productViewAll';
        var link = document.createElement('a');
        link.className = 'button button-view-all animate-arrow';
        link.href = searchUrl + query;
        link.textContent = term;
        viewAll.appendChild(link);

        holder.appendChild(window.HDSearchFuzzy.headingFor(term));
        holder.appendChild(grid);
        holder.appendChild(viewAll);

        if (window.sharedFunctions && window.sharedFunctions.swapHoverVideoProductCard) {
          window.sharedFunctions.swapHoverVideoProductCard();
        }
      })
      .catch(function () {
        holder.dataset.loaded = '';
      });
  }

  function run() {
    var params = new URLSearchParams(window.location.search);
    var original = params.get('hd_from');

    // Arrived here from a correction: explain what happened.
    if (original) {
      var notice = window.HDSearchFuzzy.noticeHtml(original, params.get('q') || '');
      var anchor =
        document.querySelector('[id^="SearchSection"] .page-header') ||
        document.querySelector('#CollectionProductGrid');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(notice, anchor.nextSibling);
      }
      // Never correct twice (no redirect loops), but if the correction itself
      // found nothing, still rescue the page with popular-search products.
      loadFallbackGrid(params.get('q') || original);
      return;
    }

    var data = window.hdSearchNoResults;
    if (!data || !data.terms) return;

    var fix = window.HDSearchFuzzy.correct(data.terms);
    if (!fix.changed) {
      loadFallbackGrid(data.terms);
      return;
    }

    params.set('q', fix.corrected);
    params.set('hd_from', data.terms);
    if (!params.get('type')) params.set('type', 'product');
    if (!params.get('options[prefix]')) params.set('options[prefix]', 'last');
    window.location.replace(window.location.pathname + '?' + params.toString());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
